// Centralized data management for consistent data across all assessment pages
// This ensures all pages use the same data structure and values
// SRS v1.1 Compliant: Property Type → Department → Touchpoint → Title → Standards

// === SRS v1.1 Core Data Types ===

export interface StandardItem {
  id: string;
  question: string;
  weight?: number; // Optional SRS weight field
  subTouchpointTag: string;
}

export interface SubTouchpoint {
  id: string;
  name: string;
  code: string;
  color: string;
}

export interface Title {
  id: string;
  name: string;
  standards: StandardItem[];
  subTouchpoints?: SubTouchpoint[]; // Optional for titles that have sub-touchpoints
}

export interface Touchpoint {
  id: string;
  name: string;
  titles: Title[]; // SRS v1.1: Touchpoints contain Titles
}

export interface Department {
  id: string;
  name: string;
  touchpoints: Touchpoint[];
}

export interface SRSPropertyType {
  id: string;
  name: string;
  departments: Department[];
}

// === Legacy Property Types (for backward compatibility) ===
export const PROPERTY_TYPES = {
  Hotels: {
    'Front Of House': [
      'RESERVATION',
      'BELLMAN ARRIVAL',
      'CHECK IN',
      'CHECK OUT',
      'GUEST SERVICES/CONCIERGE',
      'DOORMAN DEPARTURE',
      'TRANSPORT',
      'WAKE-UP CALL',
    ],
    HOUSEKEEPING: [
      'HOUSEKEEPING - ARRIVAL',
      'LAUNDRY',
      'SERVICING',
      'ROOM - PRODUCT & FACILITIES',
    ],
    'F&B': [
      'RESTAURANT SERVICE',
      'BAR SERVICE',
      'ROOM SERVICE',
      'BUFFET SERVICE',
    ],
  },
  Restaurants: {
    'F&B': [
      'GREETING',
      'SEATING',
      'ORDER TAKING',
      'FOOD SERVICE',
      'PAYMENT',
      'FAREWELL',
    ],
  },
  'Residential Compound': {
    'Customer Services Center': ['FRONT DESK', 'RECEPTION', 'CUSTOMER SUPPORT'],
    'Sales Team': ['SALES PRESENTATION', 'CLIENT MEETING', 'FOLLOW UP'],
    'Product Knowledge': ['AMENITIES', 'FACILITIES', 'SERVICES'],
    'Face To Face Spot Check': [
      'PERSONAL INTERACTION',
      'SERVICE DELIVERY',
      'COMMUNICATION',
    ],
  },
} as const;

// Type definitions for legacy compatibility
export type PropertyType = keyof typeof PROPERTY_TYPES;
export type LegacyDepartment<T extends PropertyType> =
  keyof (typeof PROPERTY_TYPES)[T];
export type LegacyTouchpoint<
  T extends PropertyType,
  D extends LegacyDepartment<T>,
> = (typeof PROPERTY_TYPES)[T][D] extends readonly string[]
  ? (typeof PROPERTY_TYPES)[T][D][number]
  : never;

// === SRS v1.1 Structured Property Types ===

export const SRS_PROPERTY_TYPES: SRSPropertyType[] = [
  {
    id: 'hotels',
    name: 'Hotels',
    departments: [
      {
        id: 'front-of-house',
        name: 'Front Of House',
        touchpoints: [
          {
            id: 'reservation',
            name: 'RESERVATION',
            titles: [
              {
                id: 'phone-reservation-process',
                name: 'Phone Reservation Process',
                standards: [
                  {
                    id: 'phone-answer-time',
                    question: 'Was the phone answered within 3 rings?',
                    subTouchpointTag: 'Response Time',
                    weight: 1,
                  },
                  {
                    id: 'proper-greeting',
                    question:
                      'Did the agent use the proper greeting including hotel name?',
                    subTouchpointTag: 'Professional Greeting',
                    weight: 1,
                  },
                  {
                    id: 'rate-communication',
                    question:
                      'Were room rates and availability clearly communicated?',
                    subTouchpointTag: 'Information Delivery',
                    weight: 2,
                  },
                  {
                    id: 'guest-info-collection',
                    question: 'Was guest information collected accurately?',
                    subTouchpointTag: 'Data Collection',
                    weight: 2,
                  },
                  {
                    id: 'confirmation-provided',
                    question: 'Was confirmation provided to the guest?',
                    subTouchpointTag: 'Confirmation Process',
                    weight: 1,
                  },
                ],
              },
              {
                id: 'online-reservation-system',
                name: 'Online Reservation System',
                standards: [
                  {
                    id: 'room-types-display',
                    question:
                      'Are all room types displayed with clear descriptions?',
                    subTouchpointTag: 'System Interface',
                    weight: 1,
                  },
                  {
                    id: 'booking-user-friendly',
                    question:
                      'Is the booking process user-friendly and intuitive?',
                    subTouchpointTag: 'User Experience',
                    weight: 2,
                  },
                  {
                    id: 'auto-confirmation-email',
                    question: 'Are confirmation emails sent automatically?',
                    subTouchpointTag: 'Automated Communication',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'bellman-arrival',
            name: 'BELLMAN ARRIVAL',
            titles: [
              {
                id: 'guest-greeting-arrival',
                name: 'Guest Greeting & Arrival Service',
                standards: [
                  {
                    id: 'bellman-greeting-time',
                    question:
                      'Did the bellman greet the guest within 30 seconds of vehicle arrival?',
                    subTouchpointTag: 'Response Time',
                    weight: 2,
                  },
                  {
                    id: 'uniform-presentation',
                    question:
                      'Was the bellman in proper uniform and well-groomed?',
                    subTouchpointTag: 'Professional Appearance',
                    weight: 1,
                  },
                  {
                    id: 'luggage-assistance',
                    question:
                      'Was luggage assistance offered and provided efficiently?',
                    subTouchpointTag: 'Service Proactivity',
                    weight: 3,
                  },
                  {
                    id: 'door-opening',
                    question: 'Were vehicle doors opened for guests?',
                    subTouchpointTag: 'Courtesy Service',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'check-in',
            name: 'CHECK IN',
            titles: [
              {
                id: 'front-desk-check-in',
                name: 'Front Desk Check-in Process',
                standards: [
                  {
                    id: 'checkin-greeting',
                    question:
                      'Was the guest greeted warmly upon approach to front desk?',
                    subTouchpointTag: 'Professional Greeting',
                    weight: 2,
                  },
                  {
                    id: 'verification-process',
                    question:
                      'Was identification and reservation verified efficiently?',
                    subTouchpointTag: 'Process Efficiency',
                    weight: 2,
                  },
                  {
                    id: 'room-assignment',
                    question:
                      'Was room assignment explained clearly with amenities mentioned?',
                    subTouchpointTag: 'Information Delivery',
                    weight: 2,
                  },
                  {
                    id: 'key-card-explanation',
                    question:
                      'Were key cards and hotel facilities explained properly?',
                    subTouchpointTag: 'Guest Education',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'check-out',
            name: 'CHECK OUT',
            titles: [
              {
                id: 'front-desk-check-out',
                name: 'Front Desk Check-out Process',
                standards: [
                  {
                    id: 'checkout-greeting',
                    question:
                      'Was the guest greeted upon approach for check-out?',
                    subTouchpointTag: 'Professional Greeting',
                    weight: 1,
                  },
                  {
                    id: 'bill-review',
                    question:
                      'Was the bill reviewed and explained clearly to the guest?',
                    subTouchpointTag: 'Billing Transparency',
                    weight: 3,
                  },
                  {
                    id: 'payment-processing',
                    question:
                      'Was payment processed efficiently without errors?',
                    subTouchpointTag: 'Process Efficiency',
                    weight: 2,
                  },
                  {
                    id: 'feedback-collection',
                    question: 'Was guest feedback about their stay solicited?',
                    subTouchpointTag: 'Guest Satisfaction',
                    weight: 2,
                  },
                  {
                    id: 'departure-assistance',
                    question:
                      'Was transportation or departure assistance offered?',
                    subTouchpointTag: 'Service Proactivity',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'guest-services-concierge',
            name: 'GUEST SERVICES/CONCIERGE',
            titles: [
              {
                id: 'concierge-assistance',
                name: 'Concierge Service Standards',
                standards: [
                  {
                    id: 'concierge-availability',
                    question:
                      'Was concierge service readily available when needed?',
                    subTouchpointTag: 'Service Availability',
                    weight: 2,
                  },
                  {
                    id: 'local-knowledge',
                    question:
                      'Did staff demonstrate excellent local area knowledge?',
                    subTouchpointTag: 'Expertise Demonstration',
                    weight: 3,
                  },
                  {
                    id: 'reservation-assistance',
                    question:
                      'Were restaurant/entertainment reservations handled efficiently?',
                    subTouchpointTag: 'Booking Efficiency',
                    weight: 2,
                  },
                  {
                    id: 'follow-up-confirmation',
                    question: 'Was follow-up provided on requested services?',
                    subTouchpointTag: 'Service Follow-up',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'doorman-departure',
            name: 'DOORMAN DEPARTURE',
            titles: [
              {
                id: 'departure-service',
                name: 'Doorman Departure Service',
                standards: [
                  {
                    id: 'departure-greeting',
                    question:
                      'Did the doorman acknowledge guests upon departure?',
                    subTouchpointTag: 'Professional Greeting',
                    weight: 1,
                  },
                  {
                    id: 'luggage-retrieval',
                    question: 'Was luggage retrieved and loaded efficiently?',
                    subTouchpointTag: 'Luggage Service',
                    weight: 3,
                  },
                  {
                    id: 'transportation-coordination',
                    question:
                      'Was transportation coordinated smoothly (taxi, car service, etc.)?',
                    subTouchpointTag: 'Transportation Coordination',
                    weight: 2,
                  },
                  {
                    id: 'farewell-courtesy',
                    question:
                      'Was a courteous farewell and thanks for choosing the hotel expressed?',
                    subTouchpointTag: 'Professional Farewell',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'transport',
            name: 'TRANSPORT',
            titles: [
              {
                id: 'transportation-service',
                name: 'Hotel Transportation Service',
                standards: [
                  {
                    id: 'transport-timeliness',
                    question: 'Did transportation arrive on time as scheduled?',
                    subTouchpointTag: 'Punctuality',
                    weight: 3,
                  },
                  {
                    id: 'vehicle-condition',
                    question:
                      'Was the vehicle clean, well-maintained, and comfortable?',
                    subTouchpointTag: 'Vehicle Standards',
                    weight: 2,
                  },
                  {
                    id: 'driver-professionalism',
                    question:
                      'Was the driver professional, courteous, and knowledgeable?',
                    subTouchpointTag: 'Driver Standards',
                    weight: 2,
                  },
                  {
                    id: 'route-efficiency',
                    question:
                      'Was the most efficient route taken to the destination?',
                    subTouchpointTag: 'Route Optimization',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'wake-up-call',
            name: 'WAKE-UP CALL',
            titles: [
              {
                id: 'wake-up-service',
                name: 'Wake-up Call Service Standards',
                standards: [
                  {
                    id: 'call-timeliness',
                    question:
                      'Was the wake-up call placed at the exact requested time?',
                    subTouchpointTag: 'Punctuality',
                    weight: 3,
                  },
                  {
                    id: 'call-courtesy',
                    question:
                      'Was the wake-up call delivered courteously with proper greeting?',
                    subTouchpointTag: 'Professional Greeting',
                    weight: 1,
                  },
                  {
                    id: 'call-clarity',
                    question:
                      'Was the call clear and audible without technical issues?',
                    subTouchpointTag: 'Communication Quality',
                    weight: 2,
                  },
                  {
                    id: 'weather-info',
                    question:
                      'Was weather information or daily highlights provided when appropriate?',
                    subTouchpointTag: 'Value-Added Service',
                    weight: 1,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'housekeeping',
        name: 'HOUSEKEEPING',
        touchpoints: [
          {
            id: 'housekeeping-arrival',
            name: 'HOUSEKEEPING - ARRIVAL',
            titles: [
              {
                id: 'room-preparation-standards',
                name: 'Room Preparation Standards',
                standards: [
                  {
                    id: 'floor-cleanliness',
                    question:
                      'Were the carpet/tiles/wood flooring clean and free of stains/dust?',
                    subTouchpointTag: 'Floor Cleanliness',
                    weight: 2,
                  },
                  {
                    id: 'surface-cleanliness',
                    question:
                      'Were all walls, doors, baseboards clean and free of marks/dirt/smudges/dust?',
                    subTouchpointTag: 'Surface Cleanliness',
                    weight: 2,
                  },
                  {
                    id: 'ceiling-fixtures',
                    question:
                      'Were ceilings, vents, smoke detectors and sprinklers clean and free of any dust?',
                    subTouchpointTag: 'Ceiling & Fixtures',
                    weight: 1,
                  },
                  {
                    id: 'comfortable-temperature',
                    question:
                      'Was the room at a comfortable temperature on arrival?',
                    subTouchpointTag: 'Climate Control',
                    weight: 2,
                  },
                  {
                    id: 'odor-free',
                    question: 'Was the room free of odor on arrival?',
                    subTouchpointTag: 'Air Quality',
                    weight: 3,
                  },
                ],
              },
              {
                id: 'bed-linen-standards',
                name: 'Bed & Linen Standards',
                standards: [
                  {
                    id: 'bed-neatly-made',
                    question:
                      'Was the bed neatly made with clean linen, free of stains and tears?',
                    subTouchpointTag: 'Bed Preparation',
                    weight: 3,
                  },
                  {
                    id: 'headboard-condition',
                    question:
                      'Was the headboard in good condition with clean bedspread/blankets?',
                    subTouchpointTag: 'Bed Accessories',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'laundry',
            name: 'LAUNDRY',
            titles: [
              {
                id: 'laundry-service-standards',
                name: 'Laundry Service Standards',
                standards: [
                  {
                    id: 'laundry-pickup-time',
                    question:
                      'Was laundry picked up within the promised time frame?',
                    subTouchpointTag: 'Service Timeliness',
                    weight: 2,
                  },
                  {
                    id: 'laundry-quality',
                    question:
                      'Were items cleaned to high standard without damage?',
                    subTouchpointTag: 'Service Quality',
                    weight: 3,
                  },
                  {
                    id: 'laundry-delivery',
                    question:
                      'Was laundry delivered on time and properly packaged?',
                    subTouchpointTag: 'Delivery Standards',
                    weight: 2,
                  },
                  {
                    id: 'laundry-inventory',
                    question:
                      'Were all items returned with accurate inventory tracking?',
                    subTouchpointTag: 'Inventory Management',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'servicing',
            name: 'SERVICING',
            titles: [
              {
                id: 'daily-housekeeping',
                name: 'Daily Housekeeping Service',
                standards: [
                  {
                    id: 'service-timing',
                    question:
                      'Was housekeeping service completed within standard time window?',
                    subTouchpointTag: 'Service Timing',
                    weight: 2,
                  },
                  {
                    id: 'room-cleaning-thoroughness',
                    question:
                      'Was the room cleaned thoroughly including all surfaces and fixtures?',
                    subTouchpointTag: 'Cleaning Thoroughness',
                    weight: 3,
                  },
                  {
                    id: 'amenities-replenishment',
                    question:
                      'Were amenities (towels, toiletries, etc.) replenished adequately?',
                    subTouchpointTag: 'Amenity Management',
                    weight: 2,
                  },
                  {
                    id: 'service-discretion',
                    question:
                      'Was service provided with appropriate discretion and minimal disturbance?',
                    subTouchpointTag: 'Guest Privacy',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'room-product-facilities',
            name: 'ROOM - PRODUCT & FACILITIES',
            titles: [
              {
                id: 'room-amenities-standards',
                name: 'Room Amenities & Facilities Standards',
                standards: [
                  {
                    id: 'bathroom-supplies',
                    question:
                      'Were all bathroom supplies and toiletries adequately stocked and quality?',
                    subTouchpointTag: 'Bathroom Amenities',
                    weight: 2,
                  },
                  {
                    id: 'room-technology',
                    question:
                      'Were all in-room technology systems (TV, internet, phone) functioning properly?',
                    subTouchpointTag: 'Technology Systems',
                    weight: 2,
                  },
                  {
                    id: 'furniture-condition',
                    question:
                      'Was all furniture in good condition and properly arranged?',
                    subTouchpointTag: 'Furniture Standards',
                    weight: 1,
                  },
                  {
                    id: 'room-safety-features',
                    question:
                      'Were all safety features (locks, safe, emergency info) functioning and accessible?',
                    subTouchpointTag: 'Safety Systems',
                    weight: 3,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'fb',
        name: 'F&B',
        touchpoints: [
          {
            id: 'restaurant-service',
            name: 'RESTAURANT SERVICE',
            titles: [
              {
                id: 'fine-dining-experience',
                name: 'Fine Dining Experience',
                standards: [
                  {
                    id: 'greeting-within-time',
                    question:
                      'Was the guest greeted within 30 seconds of seating?',
                    subTouchpointTag: 'Response Time',
                    weight: 2,
                  },
                  {
                    id: 'menu-presentation',
                    question:
                      'Were menus presented in excellent condition and explained properly?',
                    subTouchpointTag: 'Menu Service',
                    weight: 1,
                  },
                  {
                    id: 'order-accuracy',
                    question:
                      'Was the order taken accurately and repeated back to the guest?',
                    subTouchpointTag: 'Order Taking',
                    weight: 3,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'restaurants',
    name: 'Restaurants',
    departments: [
      {
        id: 'fb',
        name: 'F&B',
        touchpoints: [
          {
            id: 'greeting',
            name: 'GREETING',
            titles: [
              {
                id: 'customer-welcome-standards',
                name: 'Customer Welcome Standards',
                standards: [
                  {
                    id: 'greeting-time',
                    question:
                      'Was the guest greeted within 30 seconds of arrival?',
                    subTouchpointTag: 'Response Time',
                    weight: 2,
                  },
                  {
                    id: 'welcome-phrases',
                    question:
                      'Did staff use appropriate welcome phrases and smile?',
                    subTouchpointTag: 'Professional Greeting',
                    weight: 2,
                  },
                  {
                    id: 'eye-contact',
                    question: 'Was eye contact maintained during the greeting?',
                    subTouchpointTag: 'Personal Connection',
                    weight: 1,
                  },
                  {
                    id: 'seating-assistance',
                    question:
                      'Were guests offered assistance with seating preferences?',
                    subTouchpointTag: 'Service Proactivity',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'seating',
            name: 'SEATING',
            titles: [
              {
                id: 'table-assignment-standards',
                name: 'Table Assignment & Setup Standards',
                standards: [
                  {
                    id: 'table-availability',
                    question:
                      'Was an appropriate table available and clean upon seating?',
                    subTouchpointTag: 'Table Readiness',
                    weight: 2,
                  },
                  {
                    id: 'seating-preferences',
                    question:
                      'Were guest seating preferences accommodated when possible?',
                    subTouchpointTag: 'Guest Preferences',
                    weight: 1,
                  },
                  {
                    id: 'table-setup',
                    question:
                      'Was the table properly set with clean utensils, napkins, and condiments?',
                    subTouchpointTag: 'Table Setup',
                    weight: 2,
                  },
                  {
                    id: 'menu-presentation',
                    question:
                      'Were menus presented promptly and in good condition?',
                    subTouchpointTag: 'Menu Service',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'food-service',
            name: 'FOOD SERVICE',
            titles: [
              {
                id: 'order-taking-service',
                name: 'Order Taking & Food Service',
                standards: [
                  {
                    id: 'order-accuracy',
                    question:
                      'Was the order taken accurately and confirmed with the guest?',
                    subTouchpointTag: 'Order Accuracy',
                    weight: 3,
                  },
                  {
                    id: 'food-quality',
                    question:
                      'Was food served at proper temperature and meeting quality standards?',
                    subTouchpointTag: 'Food Quality',
                    weight: 3,
                  },
                  {
                    id: 'service-timing',
                    question:
                      'Was food served within reasonable time after ordering?',
                    subTouchpointTag: 'Service Timing',
                    weight: 2,
                  },
                  {
                    id: 'presentation',
                    question:
                      'Was food presented attractively and according to standards?',
                    subTouchpointTag: 'Food Presentation',
                    weight: 2,
                  },
                  {
                    id: 'dietary-accommodations',
                    question:
                      'Were special dietary requests handled appropriately?',
                    subTouchpointTag: 'Special Requests',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'payment',
            name: 'PAYMENT',
            titles: [
              {
                id: 'billing-payment-process',
                name: 'Billing & Payment Processing',
                standards: [
                  {
                    id: 'bill-accuracy',
                    question: 'Was the bill accurate and itemized clearly?',
                    subTouchpointTag: 'Billing Accuracy',
                    weight: 3,
                  },
                  {
                    id: 'payment-options',
                    question:
                      'Were multiple payment options available and explained?',
                    subTouchpointTag: 'Payment Flexibility',
                    weight: 1,
                  },
                  {
                    id: 'processing-efficiency',
                    question: 'Was payment processed quickly and efficiently?',
                    subTouchpointTag: 'Process Efficiency',
                    weight: 2,
                  },
                  {
                    id: 'receipt-provision',
                    question: 'Was a receipt provided promptly and accurately?',
                    subTouchpointTag: 'Documentation',
                    weight: 1,
                  },
                ],
              },
            ],
          },
          {
            id: 'farewell',
            name: 'FAREWELL',
            titles: [
              {
                id: 'departure-service',
                name: 'Guest Departure Service',
                standards: [
                  {
                    id: 'gratitude-expression',
                    question:
                      "Did staff express genuine gratitude for the guest's visit?",
                    subTouchpointTag: 'Appreciation',
                    weight: 2,
                  },
                  {
                    id: 'satisfaction-inquiry',
                    question:
                      'Was guest satisfaction with the meal and service inquired about?',
                    subTouchpointTag: 'Feedback Collection',
                    weight: 1,
                  },
                  {
                    id: 'return-invitation',
                    question:
                      'Were guests invited to return in a genuine manner?',
                    subTouchpointTag: 'Relationship Building',
                    weight: 1,
                  },
                  {
                    id: 'departure-assistance',
                    question:
                      'Was assistance offered for departure needs (coat, directions, etc.)?',
                    subTouchpointTag: 'Service Completion',
                    weight: 1,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'residential-compound',
    name: 'Residential Compound',
    departments: [
      {
        id: 'customer-services-center',
        name: 'Customer Services Center',
        touchpoints: [
          {
            id: 'front-desk',
            name: 'FRONT DESK',
            titles: [
              {
                id: 'visitor-registration-process',
                name: 'Visitor Registration Process',
                standards: [
                  {
                    id: 'info-collection',
                    question:
                      'Was visitor information collected completely and accurately?',
                    subTouchpointTag: 'Data Collection',
                    weight: 3,
                  },
                  {
                    id: 'id-verification',
                    question:
                      'Was identification verified according to security protocols?',
                    subTouchpointTag: 'Security Verification',
                    weight: 3,
                  },
                  {
                    id: 'access-credentials',
                    question:
                      'Were visitors provided with appropriate access credentials?',
                    subTouchpointTag: 'Access Management',
                    weight: 2,
                  },
                  {
                    id: 'process-efficiency',
                    question:
                      'Was the registration process completed in a timely manner?',
                    subTouchpointTag: 'Process Efficiency',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'reception',
            name: 'RECEPTION',
            titles: [
              {
                id: 'guest-reception-standards',
                name: 'Guest Reception Standards',
                standards: [
                  {
                    id: 'professional-greeting',
                    question:
                      'Were guests greeted professionally and warmly upon arrival?',
                    subTouchpointTag: 'Professional Greeting',
                    weight: 2,
                  },
                  {
                    id: 'information-provision',
                    question:
                      'Was accurate information provided about compound services and facilities?',
                    subTouchpointTag: 'Information Service',
                    weight: 2,
                  },
                  {
                    id: 'assistance-offering',
                    question:
                      'Was appropriate assistance offered for guest needs?',
                    subTouchpointTag: 'Service Proactivity',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'customer-support',
            name: 'CUSTOMER SUPPORT',
            titles: [
              {
                id: 'support-service-quality',
                name: 'Customer Support Service Quality',
                standards: [
                  {
                    id: 'issue-resolution',
                    question:
                      'Were customer issues addressed promptly and effectively?',
                    subTouchpointTag: 'Problem Resolution',
                    weight: 3,
                  },
                  {
                    id: 'support-accessibility',
                    question:
                      'Were support services easily accessible to residents and visitors?',
                    subTouchpointTag: 'Service Accessibility',
                    weight: 2,
                  },
                  {
                    id: 'follow-up-service',
                    question:
                      'Was appropriate follow-up provided after service delivery?',
                    subTouchpointTag: 'Service Follow-up',
                    weight: 2,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'sales-team',
        name: 'Sales Team',
        touchpoints: [
          {
            id: 'sales-presentation',
            name: 'SALES PRESENTATION',
            titles: [
              {
                id: 'presentation-quality',
                name: 'Sales Presentation Standards',
                standards: [
                  {
                    id: 'presentation-preparation',
                    question:
                      'Was the sales presentation well-prepared and organized?',
                    subTouchpointTag: 'Preparation Quality',
                    weight: 2,
                  },
                  {
                    id: 'product-knowledge',
                    question:
                      'Did the salesperson demonstrate comprehensive product knowledge?',
                    subTouchpointTag: 'Product Expertise',
                    weight: 3,
                  },
                  {
                    id: 'client-engagement',
                    question:
                      'Were clients actively engaged throughout the presentation?',
                    subTouchpointTag: 'Engagement Quality',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'client-meeting',
            name: 'CLIENT MEETING',
            titles: [
              {
                id: 'meeting-conduct-standards',
                name: 'Client Meeting Conduct Standards',
                standards: [
                  {
                    id: 'meeting-preparation',
                    question:
                      'Were meetings well-prepared with relevant materials and information?',
                    subTouchpointTag: 'Meeting Preparation',
                    weight: 2,
                  },
                  {
                    id: 'professional-communication',
                    question:
                      'Was communication professional and tailored to client needs?',
                    subTouchpointTag: 'Communication Quality',
                    weight: 2,
                  },
                  {
                    id: 'needs-assessment',
                    question:
                      'Were client needs properly assessed and addressed?',
                    subTouchpointTag: 'Needs Analysis',
                    weight: 3,
                  },
                ],
              },
            ],
          },
          {
            id: 'follow-up',
            name: 'FOLLOW UP',
            titles: [
              {
                id: 'follow-up-service-standards',
                name: 'Follow-up Service Standards',
                standards: [
                  {
                    id: 'timely-follow-up',
                    question:
                      'Was follow-up communication initiated within appropriate timeframes?',
                    subTouchpointTag: 'Follow-up Timing',
                    weight: 2,
                  },
                  {
                    id: 'comprehensive-follow-up',
                    question:
                      'Did follow-up address all client questions and concerns?',
                    subTouchpointTag: 'Service Completeness',
                    weight: 3,
                  },
                  {
                    id: 'relationship-building',
                    question:
                      'Was follow-up focused on building long-term client relationships?',
                    subTouchpointTag: 'Relationship Management',
                    weight: 2,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'product-knowledge',
        name: 'Product Knowledge',
        touchpoints: [
          {
            id: 'amenities',
            name: 'AMENITIES',
            titles: [
              {
                id: 'amenity-knowledge-standards',
                name: 'Amenity Knowledge & Presentation',
                standards: [
                  {
                    id: 'amenity-expertise',
                    question:
                      'Did staff demonstrate comprehensive knowledge of all amenities?',
                    subTouchpointTag: 'Knowledge Depth',
                    weight: 3,
                  },
                  {
                    id: 'amenity-benefits',
                    question:
                      'Were amenity benefits clearly explained to prospects?',
                    subTouchpointTag: 'Benefit Communication',
                    weight: 2,
                  },
                  {
                    id: 'amenity-access',
                    question:
                      'Were amenity access procedures clearly communicated?',
                    subTouchpointTag: 'Access Information',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'facilities',
            name: 'FACILITIES',
            titles: [
              {
                id: 'facility-knowledge-standards',
                name: 'Facility Knowledge & Presentation',
                standards: [
                  {
                    id: 'facility-expertise',
                    question:
                      'Did staff demonstrate thorough knowledge of all facilities?',
                    subTouchpointTag: 'Facility Expertise',
                    weight: 3,
                  },
                  {
                    id: 'facility-maintenance',
                    question:
                      'Were facilities properly maintained and presented?',
                    subTouchpointTag: 'Maintenance Standards',
                    weight: 2,
                  },
                  {
                    id: 'usage-guidelines',
                    question:
                      'Were facility usage guidelines clearly communicated?',
                    subTouchpointTag: 'Usage Information',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'services',
            name: 'SERVICES',
            titles: [
              {
                id: 'service-knowledge-standards',
                name: 'Service Knowledge & Delivery',
                standards: [
                  {
                    id: 'service-portfolio',
                    question:
                      'Did staff demonstrate comprehensive knowledge of all available services?',
                    subTouchpointTag: 'Service Knowledge',
                    weight: 3,
                  },
                  {
                    id: 'service-procedures',
                    question:
                      'Were service procedures and requirements clearly explained?',
                    subTouchpointTag: 'Procedure Clarity',
                    weight: 2,
                  },
                  {
                    id: 'service-benefits',
                    question:
                      'Were service benefits and value propositions clearly communicated?',
                    subTouchpointTag: 'Value Communication',
                    weight: 2,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'face-to-face-spot-check',
        name: 'Face To Face Spot Check',
        touchpoints: [
          {
            id: 'personal-interaction',
            name: 'PERSONAL INTERACTION',
            titles: [
              {
                id: 'interaction-quality-standards',
                name: 'Personal Interaction Quality',
                standards: [
                  {
                    id: 'interpersonal-skills',
                    question:
                      'Did staff demonstrate excellent interpersonal communication skills?',
                    subTouchpointTag: 'Communication Skills',
                    weight: 3,
                  },
                  {
                    id: 'personal-attention',
                    question:
                      'Was appropriate personal attention given to each individual?',
                    subTouchpointTag: 'Individual Focus',
                    weight: 2,
                  },
                  {
                    id: 'rapport-building',
                    question:
                      'Was effort made to build rapport and connection?',
                    subTouchpointTag: 'Relationship Building',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'service-delivery',
            name: 'SERVICE DELIVERY',
            titles: [
              {
                id: 'service-execution-standards',
                name: 'Service Execution Standards',
                standards: [
                  {
                    id: 'service-quality',
                    question:
                      'Was service delivered to high quality standards?',
                    subTouchpointTag: 'Quality Standards',
                    weight: 3,
                  },
                  {
                    id: 'service-efficiency',
                    question:
                      'Was service delivered efficiently and within expected timeframes?',
                    subTouchpointTag: 'Service Efficiency',
                    weight: 2,
                  },
                  {
                    id: 'service-consistency',
                    question:
                      'Was service delivery consistent with established standards?',
                    subTouchpointTag: 'Service Consistency',
                    weight: 2,
                  },
                ],
              },
            ],
          },
          {
            id: 'communication',
            name: 'COMMUNICATION',
            titles: [
              {
                id: 'communication-effectiveness',
                name: 'Communication Effectiveness Standards',
                standards: [
                  {
                    id: 'clarity-of-communication',
                    question:
                      'Was communication clear, concise, and easily understood?',
                    subTouchpointTag: 'Communication Clarity',
                    weight: 3,
                  },
                  {
                    id: 'active-listening',
                    question: 'Did staff demonstrate active listening skills?',
                    subTouchpointTag: 'Listening Skills',
                    weight: 2,
                  },
                  {
                    id: 'appropriate-language',
                    question:
                      'Was appropriate and professional language used throughout?',
                    subTouchpointTag: 'Language Professionalism',
                    weight: 2,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

// Shared assessment data structure (updated for SRS v1.1)
export interface SharedAssessment {
  id: string;
  propertyType: PropertyType;
  department: string;
  touchpoint: string;
  title: string;
  dateAssigned: string;
  assignedTo: string;
  assignedBy: string;
  dueDate: string;
  score: string;
  status: 'Draft' | 'Submitted' | 'Approved' | 'Pending';
}

// Shared standard structure (updated for SRS v1.1 with weights)
export interface SharedStandard {
  id: number;
  question: string;
  subTouchpointTag: string;
  status: 'yes' | 'no' | 'na' | null;
  notes: string;
  weight?: number; // SRS v1.1: Optional weight field
}

// Shared title structure
export interface SharedTitle {
  id: number;
  name: string;
  standards: SharedStandard[];
}

// Shared assessment data for perform page
export interface SharedAssessmentData {
  touchpoint: string;
  department: string;
  propertyType: PropertyType;
  titles: SharedTitle[];
  mediaFiles: { type: 'photo' | 'audio'; url: string; name: string }[];
}

// Shared assessment data structure
export interface SharedAssessment {
  id: string;
  propertyName: string;
  propertyType: PropertyType;
  department: string;
  touchpoint: string;
  title: string;
  dateAssigned: string;
  assignedTo: string;
  assignedBy: string;
  dueDate: string;
  score: string;
  status: 'Draft' | 'Submitted' | 'Approved' | 'Pending';
}

// Shared standard structure
export interface SharedStandard {
  id: number;
  question: string;
  subTouchpointTag: string;
  status: 'yes' | 'no' | 'na' | null;
  notes: string;
  answerId?: string; // Backend answer ID for API updates
}

// Shared title structure
export interface SharedTitle {
  id: number;
  name: string;
  standards: SharedStandard[];
}

// Shared assessment data for perform page
export interface SharedAssessmentData {
  touchpoint: string;
  department: string;
  propertyType: PropertyType;
  titles: SharedTitle[];
  mediaFiles: { type: 'photo' | 'audio'; url: string; name: string }[];
}

// Master assessment dataset - this ensures ALL pages use the same data
export const MASTER_ASSESSMENTS: SharedAssessment[] = [
  {
    id: '1',
    propertyName: 'Grand Hyatt Resort',
    propertyType: 'Hotels',
    department: 'Front Of House',
    touchpoint: 'RESERVATION',
    title: 'Phone Reservation Process',
    dateAssigned: '15-01-25',
    assignedTo: 'John Smith',
    assignedBy: 'Michael Davis',
    dueDate: '25-01-25',
    score: '95%',
    status: 'Pending',
  },
  {
    id: '2',
    propertyName: 'Downtown Boutique Hotel',
    propertyType: 'Hotels',
    department: 'HOUSEKEEPING',
    touchpoint: 'HOUSEKEEPING - ARRIVAL',
    title: 'Guest Room Preparation',
    dateAssigned: '10-01-25',
    assignedTo: 'Sarah Johnson',
    assignedBy: 'Emily Wilson',
    dueDate: '20-01-25',
    score: '88%',
    status: 'Submitted',
  },
  {
    id: '3',
    propertyName: 'The Palm Restaurant',
    propertyType: 'Restaurants',
    department: 'F&B',
    touchpoint: 'GREETING',
    title: 'Customer Welcome Process',
    dateAssigned: '05-01-25',
    assignedTo: 'Mike Williams',
    assignedBy: 'Alex Thompson',
    dueDate: '15-01-25',
    score: '92%',
    status: 'Approved',
  },
  {
    id: '4',
    propertyName: 'Seaside Compound',
    propertyType: 'Residential Compound',
    department: 'Customer Services Center',
    touchpoint: 'FRONT DESK',
    title: 'Visitor Registration',
    dateAssigned: '12-01-25',
    assignedTo: 'Lisa Chen',
    assignedBy: 'Robert Brown',
    dueDate: '22-01-25',
    score: '85%',
    status: 'Draft',
  },
  {
    id: '5',
    propertyName: 'Grand Hyatt Resort',
    propertyType: 'Hotels',
    department: 'F&B',
    touchpoint: 'RESTAURANT SERVICE',
    title: 'Fine Dining Experience',
    dateAssigned: '18-01-25',
    assignedTo: 'David Kim',
    assignedBy: 'Jennifer Lee',
    dueDate: '28-01-25',
    score: '91%',
    status: 'Pending',
  },
  {
    id: '6',
    propertyName: 'Le Chef Classic',
    propertyType: 'Restaurants',
    department: 'F&B',
    touchpoint: 'ORDER TAKING',
    title: 'Order Accuracy & Service',
    dateAssigned: '20-01-25',
    assignedTo: 'Maria Lopez',
    assignedBy: 'Carlos Rodriguez',
    dueDate: '30-01-25',
    score: '87%',
    status: 'Draft',
  },
];

// Master assessment templates for consistency (updated for SRS v1.1 with Title-based organization)
export const MASTER_ASSESSMENT_TEMPLATES: Record<string, SharedAssessmentData> =
  {
    '1': {
      touchpoint: 'RESERVATION',
      department: 'Front Of House',
      propertyType: 'Hotels',
      mediaFiles: [],
      titles: [
        {
          id: 1,
          name: 'Phone Reservation Process',
          standards: [
            {
              id: 1,
              question: 'Was the phone answered within 3 rings?',
              subTouchpointTag: 'Response Time',
              status: null,
              notes: '',
              weight: 1,
            },
            {
              id: 2,
              question:
                'Did the agent use the proper greeting including hotel name?',
              subTouchpointTag: 'Professional Greeting',
              status: null,
              notes: '',
              weight: 1,
            },
            {
              id: 3,
              question:
                'Were room rates and availability clearly communicated?',
              subTouchpointTag: 'Information Delivery',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 4,
              question: 'Was guest information collected accurately?',
              subTouchpointTag: 'Data Collection',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 5,
              question: 'Was confirmation provided to the guest?',
              subTouchpointTag: 'Confirmation Process',
              status: null,
              notes: '',
              weight: 1,
            },
          ],
        },
        {
          id: 2,
          name: 'Online Reservation System',
          standards: [
            {
              id: 6,
              question: 'Are all room types displayed with clear descriptions?',
              subTouchpointTag: 'System Interface',
              status: null,
              notes: '',
              weight: 1,
            },
            {
              id: 7,
              question: 'Is the booking process user-friendly and intuitive?',
              subTouchpointTag: 'User Experience',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 8,
              question: 'Are confirmation emails sent automatically?',
              subTouchpointTag: 'Automated Communication',
              status: null,
              notes: '',
              weight: 1,
            },
          ],
        },
      ],
    },
    '2': {
      touchpoint: 'HOUSEKEEPING - ARRIVAL',
      department: 'HOUSEKEEPING',
      propertyType: 'Hotels',
      mediaFiles: [],
      titles: [
        {
          id: 1,
          name: 'Room Preparation Standards',
          standards: [
            {
              id: 1,
              question:
                'Were the carpet/tiles/wood flooring clean and free of stains/dust?',
              subTouchpointTag: 'Floor Cleanliness',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 2,
              question:
                'Were all walls, doors, baseboards clean and free of marks/dirt/smudges/dust?',
              subTouchpointTag: 'Surface Cleanliness',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 3,
              question:
                'Were ceilings, vents, smoke detectors and sprinklers clean and free of any dust?',
              subTouchpointTag: 'Ceiling & Fixtures',
              status: null,
              notes: '',
              weight: 1,
            },
            {
              id: 4,
              question: 'Was the room at a comfortable temperature on arrival?',
              subTouchpointTag: 'Climate Control',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 5,
              question: 'Was the room free of odor on arrival?',
              subTouchpointTag: 'Air Quality',
              status: null,
              notes: '',
              weight: 3,
            },
          ],
        },
        {
          id: 2,
          name: 'Bed & Linen Standards',
          standards: [
            {
              id: 6,
              question:
                'Was the bed neatly made with clean linen, free of stains and tears?',
              subTouchpointTag: 'Bed Preparation',
              status: null,
              notes: '',
              weight: 3,
            },
            {
              id: 7,
              question:
                'Was the headboard in good condition with clean bedspread/blankets?',
              subTouchpointTag: 'Bed Accessories',
              status: null,
              notes: '',
              weight: 2,
            },
          ],
        },
      ],
    },
    '3': {
      touchpoint: 'GREETING',
      department: 'F&B',
      propertyType: 'Restaurants',
      mediaFiles: [],
      titles: [
        {
          id: 1,
          name: 'Customer Welcome Standards',
          standards: [
            {
              id: 1,
              question: 'Was the guest greeted within 30 seconds of arrival?',
              subTouchpointTag: 'Response Time',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 2,
              question: 'Did staff use appropriate welcome phrases and smile?',
              subTouchpointTag: 'Professional Greeting',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 3,
              question: 'Was eye contact maintained during the greeting?',
              subTouchpointTag: 'Personal Connection',
              status: null,
              notes: '',
              weight: 1,
            },
            {
              id: 4,
              question:
                'Were guests offered assistance with seating preferences?',
              subTouchpointTag: 'Service Proactivity',
              status: null,
              notes: '',
              weight: 1,
            },
          ],
        },
      ],
    },
    '4': {
      touchpoint: 'FRONT DESK',
      department: 'Customer Services Center',
      propertyType: 'Residential Compound',
      mediaFiles: [],
      titles: [
        {
          id: 1,
          name: 'Visitor Registration Process',
          standards: [
            {
              id: 1,
              question:
                'Was visitor information collected completely and accurately?',
              subTouchpointTag: 'Data Collection',
              status: null,
              notes: '',
              weight: 3,
            },
            {
              id: 2,
              question:
                'Was identification verified according to security protocols?',
              subTouchpointTag: 'Security Verification',
              status: null,
              notes: '',
              weight: 3,
            },
            {
              id: 3,
              question:
                'Were visitors provided with appropriate access credentials?',
              subTouchpointTag: 'Access Management',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 4,
              question:
                'Was the registration process completed in a timely manner?',
              subTouchpointTag: 'Process Efficiency',
              status: null,
              notes: '',
              weight: 2,
            },
          ],
        },
      ],
    },
    '5': {
      touchpoint: 'RESTAURANT SERVICE',
      department: 'F&B',
      propertyType: 'Hotels',
      mediaFiles: [],
      titles: [
        {
          id: 1,
          name: 'Fine Dining Experience',
          standards: [
            {
              id: 1,
              question: 'Was the guest greeted within 30 seconds of seating?',
              subTouchpointTag: 'Response Time',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 2,
              question:
                'Were menus presented in excellent condition and explained properly?',
              subTouchpointTag: 'Menu Service',
              status: null,
              notes: '',
              weight: 1,
            },
            {
              id: 3,
              question:
                'Was the order taken accurately and repeated back to the guest?',
              subTouchpointTag: 'Order Taking',
              status: null,
              notes: '',
              weight: 3,
            },
          ],
        },
      ],
    },
    '6': {
      touchpoint: 'ORDER TAKING',
      department: 'F&B',
      propertyType: 'Restaurants',
      mediaFiles: [],
      titles: [
        {
          id: 1,
          name: 'Order Accuracy & Service',
          standards: [
            {
              id: 1,
              question:
                'Was the order taken accurately with clear repetition to customer?',
              subTouchpointTag: 'Order Accuracy',
              status: null,
              notes: '',
              weight: 3,
            },
            {
              id: 2,
              question:
                'Were dietary restrictions and allergies inquired about?',
              subTouchpointTag: 'Health & Safety',
              status: null,
              notes: '',
              weight: 2,
            },
            {
              id: 3,
              question: 'Were upselling opportunities appropriately presented?',
              subTouchpointTag: 'Sales Techniques',
              status: null,
              notes: '',
              weight: 1,
            },
          ],
        },
      ],
    },
  };

// Helper functions for data consistency
export const getAssessmentById = (id: string): SharedAssessment | undefined => {
  return MASTER_ASSESSMENTS.find(assessment => assessment.id === id);
};

export const getAssessmentDataById = (
  id: string
): SharedAssessmentData | undefined => {
  return MASTER_ASSESSMENT_TEMPLATES[id];
};

export const getAssessmentsByStatus = (
  status: SharedAssessment['status']
): SharedAssessment[] => {
  return MASTER_ASSESSMENTS.filter(assessment => assessment.status === status);
};

export const getAssessmentsByPropertyType = (
  propertyType: PropertyType
): SharedAssessment[] => {
  return MASTER_ASSESSMENTS.filter(
    assessment => assessment.propertyType === propertyType
  );
};

export const getDepartmentsByPropertyType = (
  propertyType: PropertyType
): string[] => {
  return Object.keys(PROPERTY_TYPES[propertyType]);
};

export const getTouchpointsByDepartment = (
  propertyType: PropertyType,
  department: string
): string[] => {
  const propertyData = PROPERTY_TYPES[propertyType];
  if (propertyData && department in propertyData) {
    return propertyData[department as keyof typeof propertyData] as string[];
  }
  return [];
};

// Assessment status colors for consistency
export const getStatusColor = (status: SharedAssessment['status']): string => {
  switch (status) {
    case 'Draft':
      return 'bg-yellow-100 text-yellow-800';
    case 'Pending':
      return 'bg-blue-100 text-blue-800';
    case 'Submitted':
      return 'bg-purple-100 text-purple-800';
    case 'Approved':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

// Sub-touchpoint tag colors for consistency
export const getSubTouchpointColor = (tag: string): string => {
  const colors = [
    'bg-blue-100 text-blue-800',
    'bg-green-100 text-green-800',
    'bg-purple-100 text-purple-800',
    'bg-orange-100 text-orange-800',
    'bg-pink-100 text-pink-800',
    'bg-indigo-100 text-indigo-800',
    'bg-teal-100 text-teal-800',
    'bg-red-100 text-red-800',
  ];
  // Use string length and char codes for consistent color assignment
  const index =
    tag.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    colors.length;
  return colors[index];
};

// SRS v1.1 Weighted Scoring calculation (N.A excluded from denominator)
export const calculateScore = (
  standards: SharedStandard[]
): { score: number; yes: number; no: number; na: number; answered: number } => {
  const yes = standards.filter(standard => standard.status === 'yes').length;
  const no = standards.filter(standard => standard.status === 'no').length;
  const na = standards.filter(standard => standard.status === 'na').length;
  const answered = yes + no; // N.A excluded from scoring as per SRS
  const score = answered > 0 ? Math.round((yes / answered) * 100) : 0;
  return { score, yes, no, na, answered };
};

export const calculateWeightedScore = (
  standards: SharedStandard[]
): {
  score: number;
  totalWeight: number;
  achievedWeight: number;
  yes: number;
  no: number;
  na: number;
  answered: number;
} => {
  let totalWeight = 0;
  let achievedWeight = 0;
  let yes = 0;
  let no = 0;
  let na = 0;

  standards.forEach(standard => {
    const weight = standard.weight || 1; // Default weight of 1 if not specified

    if (standard.status === 'yes') {
      yes++;
      achievedWeight += weight;
      totalWeight += weight;
    } else if (standard.status === 'no') {
      no++;
      totalWeight += weight;
    } else if (standard.status === 'na') {
      na++;
      // N.A excluded from both numerator and denominator as per SRS
    }
  });

  const answered = yes + no;
  const score =
    totalWeight > 0 ? Math.round((achievedWeight / totalWeight) * 100) : 0;

  return { score, totalWeight, achievedWeight, yes, no, na, answered };
};

// === SRS v1.1 Helper Functions ===

export const getSRSPropertyTypes = (): SRSPropertyType[] => {
  return SRS_PROPERTY_TYPES;
};

export const getSRSPropertyTypeById = (
  id: string
): SRSPropertyType | undefined => {
  return SRS_PROPERTY_TYPES.find(pt => pt.id === id);
};

export const getSRSDepartmentById = (
  propertyTypeId: string,
  departmentId: string
): Department | undefined => {
  const propertyType = getSRSPropertyTypeById(propertyTypeId);
  return propertyType?.departments.find(dept => dept.id === departmentId);
};

export const getSRSTouchpointById = (
  propertyTypeId: string,
  departmentId: string,
  touchpointId: string
): Touchpoint | undefined => {
  const department = getSRSDepartmentById(propertyTypeId, departmentId);
  return department?.touchpoints.find(tp => tp.id === touchpointId);
};

export const getSRSTitleById = (
  propertyTypeId: string,
  departmentId: string,
  touchpointId: string,
  titleId: string
): Title | undefined => {
  const touchpoint = getSRSTouchpointById(
    propertyTypeId,
    departmentId,
    touchpointId
  );
  return touchpoint?.titles.find(title => title.id === titleId);
};

export const getAllTitlesForTouchpoint = (
  propertyTypeId: string,
  departmentId: string,
  touchpointId: string
): Title[] => {
  const touchpoint = getSRSTouchpointById(
    propertyTypeId,
    departmentId,
    touchpointId
  );
  return touchpoint?.titles || [];
};

export const getAllStandardsForTitle = (
  propertyTypeId: string,
  departmentId: string,
  touchpointId: string,
  titleId: string
): StandardItem[] => {
  const title = getSRSTitleById(
    propertyTypeId,
    departmentId,
    touchpointId,
    titleId
  );
  return title?.standards || [];
};

export const getSubTouchpointsForTouchpoint = (
  propertyTypeId: string,
  departmentId: string,
  touchpointId: string
): SubTouchpoint[] => {
  // For now, return sample sub-touchpoints to test the functionality
  // This will be replaced with actual template data integration later

  if (propertyTypeId === 'restaurant' && touchpointId === 'tp_host_arrival') {
    return [
      {
        id: 'sub_host_greeting',
        name: 'HOST GREETING',
        code: 'HOST_GREETING',
        color: '#3B82F6',
      },
      {
        id: 'sub_emotional_interaction_rest',
        name: 'EMOTIONAL INTERACTION',
        code: 'EMOTIONAL_INTERACTION_REST',
        color: '#10B981',
      },
    ];
  }

  if (propertyTypeId === 'restaurant' && touchpointId === 'tp_dining_service') {
    return [
      {
        id: 'sub_server_interaction',
        name: 'SERVER INTERACTION',
        code: 'SERVER_INTERACTION',
        color: '#F59E0B',
      },
      {
        id: 'sub_food_service',
        name: 'FOOD SERVICE',
        code: 'FOOD_SERVICE',
        color: '#EF4444',
      },
    ];
  }

  if (
    propertyTypeId === 'restaurant' &&
    touchpointId === 'tp_food_preparation'
  ) {
    return [
      {
        id: 'sub_food_quality',
        name: 'FOOD QUALITY',
        code: 'FOOD_QUALITY',
        color: '#8B5CF6',
      },
      {
        id: 'sub_kitchen_hygiene',
        name: 'KITCHEN HYGIENE',
        code: 'KITCHEN_HYGIENE',
        color: '#06B6D4',
      },
    ];
  }

  if (propertyTypeId === 'compound' && touchpointId === 'tp_gate_security') {
    return [
      {
        id: 'sub_visitor_management',
        name: 'VISITOR MANAGEMENT',
        code: 'VISITOR_MANAGEMENT',
        color: '#F97316',
      },
      {
        id: 'sub_security_interaction',
        name: 'SECURITY INTERACTION',
        code: 'SECURITY_INTERACTION',
        color: '#84CC16',
      },
    ];
  }

  if (propertyTypeId === 'compound' && touchpointId === 'tp_common_areas') {
    return [
      {
        id: 'sub_cleanliness',
        name: 'CLEANLINESS',
        code: 'CLEANLINESS',
        color: '#EC4899',
      },
      {
        id: 'sub_maintenance',
        name: 'MAINTENANCE',
        code: 'MAINTENANCE',
        color: '#6B7280',
      },
    ];
  }

  // Hotel sub-touchpoints
  if (propertyTypeId === 'hotel' && touchpointId === 'tp_bellman_arrival') {
    return [
      {
        id: 'sub_luggage_assistance',
        name: 'LUGGAGE ASSISTANCE',
        code: 'LUGGAGE_ASSIST',
        color: '#3B82F6',
      },
      {
        id: 'sub_guest_greeting',
        name: 'GUEST GREETING',
        code: 'GUEST_GREETING',
        color: '#10B981',
      },
    ];
  }

  if (propertyTypeId === 'hotel' && touchpointId === 'tp_check_in') {
    return [
      {
        id: 'sub_registration_process',
        name: 'REGISTRATION PROCESS',
        code: 'REGISTRATION',
        color: '#F59E0B',
      },
      {
        id: 'sub_room_assignment',
        name: 'ROOM ASSIGNMENT',
        code: 'ROOM_ASSIGN',
        color: '#EF4444',
      },
      {
        id: 'sub_key_delivery',
        name: 'KEY DELIVERY',
        code: 'KEY_DELIVERY',
        color: '#8B5CF6',
      },
    ];
  }

  if (propertyTypeId === 'hotel' && touchpointId === 'tp_back_of_house') {
    return [
      {
        id: 'sub_housekeeping_standards',
        name: 'HOUSEKEEPING STANDARDS',
        code: 'HOUSEKEEPING',
        color: '#06B6D4',
      },
      {
        id: 'sub_maintenance_response',
        name: 'MAINTENANCE RESPONSE',
        code: 'MAINTENANCE',
        color: '#F97316',
      },
    ];
  }

  // Default: return empty array for touchpoints without sub-touchpoints
  return [];
};

export const validateSubTouchpointBinding = (
  _touchpointId: string,
  _subTouchpointTag: string
): boolean => {
  return true; // Placeholder - would contain actual validation logic
};

// Property Scope Activation helpers
export interface PropertyScopeConfig {
  propertyId: string;
  activeDepartments: string[];
  activeTouchpoints: { [departmentId: string]: string[] };
}

export const getActiveConfigForProperty = (
  _propertyId: string
): PropertyScopeConfig | undefined => {
  return undefined;
};
